import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Video } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/hooks/useToast';
import { listarMasterclass, monitoreoDeMasterclass, crearMasterclass, sacarElLote, type Masterclass } from '@/api/masterclass.api';
import {
  aDatos,
  conteoDe,
  cuandoEs,
  erroresDelFormulario,
  ETIQUETA_DE_ESTADO,
  FORMULARIO_VACIO,
  porQueEseTono,
  puedeSacarLote,
  queHayQueHacer,
  tonoDe,
  type FormularioDeMasterclass,
} from '@/lib/masterclass';

/**
 * LAS MASTERCLASS DE ENRIQUE (tanda B y C).
 *
 * El negocio entero cuelga de esta pantalla: una masterclass gratis por Zoom cada semana, y
 * de la gente que entra salen las parejas que compran su entrada para el evento del 29.
 *
 * Lo que de verdad hace falta ver aquí no es la lista: es **a quién le falta una llamada**.
 * Por eso arriba va lo que hay que hacer hoy, y solo si hay algo —un aviso que aparece
 * siempre deja de ser un aviso—, y el botón que importa es «Sacar el lote», que convierte a
 * los que asistieron en tareas para mañana.
 *
 * El lote NO manda mensajes. Crea tareas que aparecen en Seguimientos y que aprueba una
 * persona. Así heredan todo lo que ya está arreglado: el corte de crisis, las bajas, los
 * contactos apartados y la ventana de envío.
 */

const COLOR_DE_TONO = {
  alerta: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  ojo: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  ok: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
} as const;

const ENTRADA = 'min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900';

/** Lo que hay que hacer hoy. Sin estado: se prueba con `renderToStaticMarkup`. */
export function QueHayQueHacerVista({ lineas }: { lineas: string[] }) {
  if (lineas.length === 0) return null;
  return (
    <Card>
      <CardBody className="space-y-2">
        <h2 className="text-sm font-semibold">Lo que falta hacer</h2>
        <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
          {lineas.map((l) => (
            <li key={l} className="flex gap-2">
              <span aria-hidden="true" className="text-amber-600">
                •
              </span>
              <span>{l}</span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

/** Una edición. Sin estado: se prueba con `renderToStaticMarkup`. */
export function MasterclassTarjeta({ m, onLote }: { m: Masterclass; onLote?: () => void }) {
  const tono = tonoDe(m);
  const porQue = porQueEseTono(m);
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="min-w-0 grow text-[15px] font-semibold">{m.titulo}</h3>
          <Badge className={COLOR_DE_TONO[tono]}>{ETIQUETA_DE_ESTADO[m.estado] ?? m.estado}</Badge>
        </div>
        <p className="text-sm text-slate-600 first-letter:uppercase dark:text-slate-300">{cuandoEs(m.inicio)}</p>
        <p className="text-sm tabular-nums text-slate-600 dark:text-slate-300">{conteoDe(m)}</p>
        {porQue && <p className="text-sm font-medium text-amber-700 dark:text-amber-300">{porQue}</p>}
        {m.loteHechoAt && <p className="text-xs text-slate-500">Las llamadas ya están en Seguimientos.</p>}
        {puedeSacarLote(m) && (
          <Button type="button" onClick={onLote}>
            Sacar el lote de llamadas
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

/** El formulario de una edición nueva. Sin estado propio: lo recibe todo por props. */
export function FormularioVista({
  form,
  errores,
  onChange,
  onGuardar,
  guardando,
}: {
  form: FormularioDeMasterclass;
  errores: Record<string, string>;
  onChange: (patch: Partial<FormularioDeMasterclass>) => void;
  onGuardar?: () => void;
  guardando?: boolean;
}) {
  const campo = (id: keyof FormularioDeMasterclass, etiqueta: string, tipo = 'text', ayuda?: string) => (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{etiqueta}</span>
      <input
        id={`mc-${id}`}
        type={tipo}
        className={ENTRADA}
        value={form[id]}
        onChange={(e) => onChange({ [id]: e.target.value } as Partial<FormularioDeMasterclass>)}
        aria-label={etiqueta}
        aria-invalid={errores[id] ? true : undefined}
      />
      {errores[id] ? <span className="text-xs text-red-600">{errores[id]}</span> : ayuda ? <span className="text-xs text-slate-500">{ayuda}</span> : null}
    </label>
  );
  return (
    <Card>
      <CardBody className="space-y-3">
        <h2 className="text-sm font-semibold">Nueva masterclass</h2>
        {campo('titulo', 'Nombre')}
        {campo('inicio', 'Día y hora', 'datetime-local', 'En hora de Santo Domingo.')}
        {campo('zoomUrl', 'Enlace de Zoom', 'text', 'Sin esto el agente no lo promete.')}
        {campo('cupo', 'Cupo', 'text', 'Déjalo vacío si no hay tope.')}
        <Button type="button" onClick={onGuardar} disabled={guardando}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Guardar
        </Button>
      </CardBody>
    </Card>
  );
}

export default function MasterclassPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<FormularioDeMasterclass>(FORMULARIO_VACIO);
  const [abierto, setAbierto] = useState(false);

  const lista = useQuery({ queryKey: ['masterclass'], queryFn: listarMasterclass });
  const monitoreo = useQuery({ queryKey: ['masterclass', 'monitoreo'], queryFn: monitoreoDeMasterclass });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['masterclass'] });
    // Las tareas del lote viven en Seguimientos: esa pantalla también cambia.
    void qc.invalidateQueries({ queryKey: ['seguimientos'] });
  };

  const guardar = useMutation({
    mutationFn: () => crearMasterclass(aDatos(form)),
    onSuccess: () => {
      toast.success('Masterclass guardada');
      setForm(FORMULARIO_VACIO);
      setAbierto(false);
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lote = useMutation({
    mutationFn: sacarElLote,
    onSuccess: (r) => {
      toast.success(r.yaEstaba ? 'Ese lote ya se había sacado' : `${r.creadas} ${r.creadas === 1 ? 'llamada' : 'llamadas'} en Seguimientos`);
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const errores = erroresDelFormulario(form);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Masterclass"
        description="Cada edición con quién se apuntó y quién entró. De aquí salen las llamadas del día siguiente."
        action={
          <Button type="button" onClick={() => setAbierto((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Nueva
          </Button>
        }
      />

      {monitoreo.data && <QueHayQueHacerVista lineas={queHayQueHacer(monitoreo.data)} />}

      {abierto && (
        <FormularioVista
          form={form}
          errores={guardar.isPending ? {} : errores}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          onGuardar={() => (Object.keys(errores).length ? toast.error('Revisa lo que está marcado') : guardar.mutate())}
          guardando={guardar.isPending}
        />
      )}

      {lista.isLoading && <p className="text-sm text-slate-500">Cargando…</p>}
      {lista.data?.length === 0 && (
        <Card>
          <CardBody className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <p className="flex items-center gap-2 font-medium">
              <Video className="h-4 w-4" aria-hidden="true" /> Todavía no hay ninguna masterclass.
            </p>
            <p>
              Crea la próxima con su día, su hora y su enlace de Zoom. Mientras no haya ninguna, el agente no inventa fechas: dice que el equipo
              escribe en cuanto se abra.
            </p>
          </CardBody>
        </Card>
      )}
      <div className="space-y-3">
        {lista.data?.map((m) => (
          <MasterclassTarjeta key={m.id} m={m} onLote={() => lote.mutate(m.id)} />
        ))}
      </div>
    </div>
  );
}
